import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

function getCategoryName(slug: string): string {
  const cat = categories.find(c => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const currentSlug = slug || 'all';
  const currentCategory = categories.find(c => c.slug === currentSlug);

  let filteredComics = currentSlug === 'all'
    ? comics
    : comics.filter(c => c.category === currentSlug);

  if (sortBy === 'popular') {
    filteredComics = [...filteredComics].sort((a, b) => b.views - a.views);
  } else if (sortBy === 'rating') {
    filteredComics = [...filteredComics].sort((a, b) => b.rating - a.rating);
  }

  const displayCategories = categories.filter(c => c.slug !== 'all');
  const pageTitle = currentCategory ? `${currentCategory.name}韩国漫画` : '全部韩国漫画';
  const pageDescription = currentCategory
    ? `妖精动漫${currentCategory.name}类韩国漫画专区，${currentCategory.description}。免费无删减下拉式阅读，海量韩漫资源等您来探索。`
    : '妖精动漫免费韩漫大全，涵盖恋爱、奇幻、动作、悬疑、搞笑、剧情、恐怖等全部分类。无删减下拉式阅读体验，海量韩国漫画免费畅读。';

  return (
    <>
      <SchemaOrg
        type="CollectionPage"
        data={{
          name: pageTitle,
          description: pageDescription,
          url: `https://yaojingdongman.com/category/${currentSlug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '妖精动漫',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://yaojingdongman.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: currentCategory ? currentCategory.name : '全部分类',
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory ? currentCategory.name : '全部分类' },
              ]}
            />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                {currentCategory ? `${currentCategory.name}韩国漫画` : '免费韩漫大全'}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                {currentCategory
                  ? currentCategory.description
                  : '浏览全部韩国漫画作品，找到您心仪的韩漫'}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              <Link
                to="/category/all"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  currentSlug === 'all'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </Link>
              {displayCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-900">{filteredComics.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序：</span>
                {[
                  { value: 'latest', label: '最新' },
                  { value: 'popular', label: '最热' },
                  { value: 'rating', label: '评分' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value as typeof sortBy)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === opt.value
                        ? 'bg-secondary-500 text-white'
                        : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5 pb-14">
                {filteredComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    title={comic.title}
                    cover={comic.cover}
                    category={comic.category}
                    categoryName={getCategoryName(comic.category)}
                    rating={comic.rating}
                    chapters={comic.chapters}
                    status={comic.status}
                    isHot={comic.isHot}
                    isNew={comic.isNew}
                    views={comic.views}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无作品，请浏览其他分类</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}